import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, FileText, Hammer, GraduationCap, ArrowRight } from "lucide-react";
import { fadeUp, stagger, viewport } from "./anim";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" });
};

const steps = [
  {
    n: "01",
    icon: MessageCircle,
    title: "Free Consultation",
    body: "We talk about your business, where time is getting lost, and where AI could actually help. If AI isn't the right fit, I'll tell you.",
  },
  {
    n: "02",
    icon: FileText,
    title: "Custom Proposal",
    body: "You get a written plan and a fixed price before any work begins. No hourly meters, no hidden fees.",
  },
  {
    n: "03",
    icon: Hammer,
    title: "Build",
    body: "I build and test your solution on my own systems first, then launch it for your business.",
  },
  {
    n: "04",
    icon: GraduationCap,
    title: "Train",
    body: "Your team learns how to use it and manage it, hands-on, at their own pace. You're not left to figure it out.",
  },
];

export const HowItWorks = () => {
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="blob w-[30rem] h-[30rem] -left-40 top-20 animate-blob" style={{ background: "rgba(233,51,198,0.13)" }} />
      <div className="blob w-[26rem] h-[26rem] right-0 bottom-0 animate-blob" style={{ background: "rgba(139,92,246,0.12)", animationDelay: "-8s" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div initial="hidden" whileInView="show" viewport={viewport} variants={stagger} className="max-w-3xl">
          <motion.div variants={fadeUp} className="font-mono-accent text-xs tracking-[0.25em] uppercase text-fuchsia-600 mb-4">
            How It Works
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-display font-bold text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
            No templates. <span className="text-gradient">No surprise invoices.</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-5 text-slate-700 text-lg leading-relaxed">
            Every project is built around your business, so every price is too. Here's what working
            with me looks like.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          variants={stagger}
          className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {steps.map((s) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                variants={fadeUp}
                data-testid={`how-step-${s.n}`}
                className="group relative overflow-hidden rounded-3xl p-7 glass-light hover:shadow-[0_30px_80px_-30px_rgba(233,51,198,0.4)] transition-shadow duration-500"
              >
                <span
                  className="absolute right-5 top-4 font-display font-extrabold text-5xl leading-none text-transparent select-none"
                  style={{ WebkitTextStroke: "1.5px rgba(233,51,198,0.25)" }}
                >
                  {s.n}
                </span>
                <div className="relative inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6"
                  style={{ background: "linear-gradient(135deg,rgba(233,51,198,0.12),rgba(139,92,246,0.12))", border: "1px solid rgba(233,51,198,0.2)" }}>
                  <Icon className="text-fuchsia-600" size={24} strokeWidth={1.8} />
                </div>
                <h3 className="relative font-display font-bold text-slate-900 text-xl tracking-tight">{s.title}</h3>
                <p className="relative mt-3 text-slate-700 leading-relaxed text-[15px]">{s.body}</p>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div initial="hidden" whileInView="show" viewport={viewport} variants={fadeUp} className="mt-12 flex justify-center">
          <button
            data-testid="how-book-button"
            onClick={() => scrollTo("#contact")}
            className="btn-glow inline-flex items-center gap-2 rounded-xl px-7 py-3.5 text-sm sm:text-base font-semibold text-white"
            style={{ background: "linear-gradient(100deg,#FF4FD8,#E933C6 55%,#8B5CF6)" }}
          >
            Book a Free Consultation <ArrowRight size={18} />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
